import React, { useState, useEffect, memo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { ArrowUpCircle, CheckCircle } from 'lucide-react';
import { DebouncedInput, DebouncedTextarea } from '../shared/DebouncedInputs';
import api, { formatErrorMessage } from '../../../../utils/api';
import { toast } from 'sonner';

const motivosND = [
  { value: 'intereses_mora', label: 'Intereses por mora' },
  { value: 'gastos_cobranza', label: 'Gastos de cobranza' },
  { value: 'diferencia_precio', label: 'Diferencia de precio' },
  { value: 'recargo', label: 'Recargo' },
  { value: 'otro', label: 'Otro' }
];

const NotaDebitoModal = memo(({ 
  open, 
  onOpenChange, 
  factura,
  onSuccess 
}) => {
  const [notaDebito, setNotaDebito] = useState({
    numero_documento: '',
    amount_clp: '',
    reason: 'intereses_mora',
    description: '',
    issue_date: new Date().toISOString().slice(0, 10)
  });
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setNotaDebito({
        numero_documento: '',
        amount_clp: '',
        reason: 'intereses_mora',
        description: '',
        issue_date: new Date().toISOString().slice(0, 10)
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!factura?.id || !notaDebito.amount_clp || !notaDebito.numero_documento) {
      toast.error('Debe ingresar el número de documento y el monto');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/finanzas/cobranzas-facturas/${factura.id}/nota-debito`, {
        numero_documento: notaDebito.numero_documento,
        amount_clp: parseFloat(notaDebito.amount_clp),
        reason: notaDebito.reason,
        description: notaDebito.description,
        issue_date: notaDebito.issue_date
      });
      
      toast.success('Nota de débito emitida correctamente');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al emitir nota de débito');
    } finally {
      setLoading(false);
    }
  };

  const formatCLP = (amount) => new Intl.NumberFormat('es-CL').format(amount || 0);

  if (!factura) return null;

  const totalFactura = factura.total_amount_clp || factura.monto_clp || 0;
  const pendiente = factura.pending_amount_clp ?? totalFactura;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <ArrowUpCircle className='w-5 h-5 text-amber-600' />
            Emitir Nota de Débito
          </DialogTitle>
          <DialogDescription>
            Factura #{factura.numero_factura} - {factura.cliente?.company || factura.cliente?.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className='space-y-4'>
          {/* Info de la factura */}
          <Card className='p-3 bg-slate-50 border-slate-200'>
            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <p className='text-slate-500'>Monto Total Factura</p>
                <p className='font-bold text-slate-900'>${formatCLP(totalFactura)}</p>
              </div>
              <div>
                <p className='text-slate-500'>Saldo Pendiente</p>
                <p className='font-bold text-amber-600'>${formatCLP(pendiente)}</p>
              </div>
            </div>
          </Card>

          {/* Formulario */}
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='nd_numero'>N° Nota de Débito *</Label>
              <DebouncedInput
                id='nd_numero'
                placeholder='Ej: ND-001'
                value={notaDebito.numero_documento}
                onChange={(e) => setNotaDebito(prev => ({ ...prev, numero_documento: e.target.value }))}
                data-testid='nd-numero-input'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='nd_amount'>Monto (CLP) *</Label>
              <Input
                id='nd_amount'
                type='number'
                placeholder='0'
                value={notaDebito.amount_clp}
                onChange={(e) => setNotaDebito(prev => ({ ...prev, amount_clp: e.target.value }))}
                data-testid='nd-amount-input'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='nd_date'>Fecha de Emisión</Label>
              <Input
                id='nd_date'
                type='date'
                value={notaDebito.issue_date}
                onChange={(e) => setNotaDebito(prev => ({ ...prev, issue_date: e.target.value }))}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='nd_reason'>Motivo</Label>
              <Select
                value={notaDebito.reason}
                onValueChange={(value) => setNotaDebito(prev => ({ ...prev, reason: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Seleccione motivo' />
                </SelectTrigger>
                <SelectContent>
                  {motivosND.map((motivo) => (
                    <SelectItem key={motivo.value} value={motivo.value}>
                      {motivo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='nd_description'>Descripción (opcional)</Label>
            <DebouncedTextarea
              id='nd_description'
              placeholder='Detalle del motivo de la nota de débito...'
              value={notaDebito.description}
              onChange={(e) => setNotaDebito(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            className='bg-amber-600 hover:bg-amber-700'
            disabled={loading || !notaDebito.amount_clp || !notaDebito.numero_documento}
            data-testid='btn-emitir-nd'
          >
            <CheckCircle className='w-4 h-4 mr-2' />
            {loading ? 'Emitiendo...' : 'Emitir ND'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

NotaDebitoModal.displayName = 'NotaDebitoModal';

export default NotaDebitoModal;
